'use client';

import { useState, useEffect } from 'react';
import { Copy, Sparkles, Calendar } from 'lucide-react';
import { Button } from '@/components/Button/ButtonV2';
import { useCustomerJourney } from '../../../context/setup';
import { DialogClone, DialogQuickAdd, StaffSelector, WeeklyScheduleCards } from './components';
import { hasProfessionalSchedule, timeToMinutes } from './utils';
import { ActionType, DayKey, PlanType, Professional } from '@/containers/CustomerJourneySetup/context/types';
import { emptySchedule } from '@/containers/CustomerJourneySetup/context/utils';
import { days } from './constants';
import DialogGuide from './components/DialogGuide';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import { trackGenericEvent } from '@/analytics';

interface TimeRange {
  start: string;
  end: string;
}

// Helper function to calculate smart time slot defaults
const calculateSmartTimeSlot = (existingSlots: { start: string; end: string }[]) => {
  // If no existing slots, return default 9:00-18:00
  if (existingSlots.length === 0) {
    return { start: '09:00', end: '18:00' };
  }

  // Find the latest end time
  const latestEndTime = existingSlots.reduce((latest, slot) => {
    const currentEndMinutes = timeToMinutes(slot.end);
    const latestMinutes = timeToMinutes(latest);
    return currentEndMinutes > latestMinutes ? slot.end : latest;
  }, '00:00');

  // Start new slot from the latest end time
  const startMinutes = timeToMinutes(latestEndTime);
  // Add 1 hour duration
  const endMinutes = startMinutes + 60;

  // Convert back to time format
  const startHour = Math.floor(startMinutes / 60);
  const startMin = startMinutes % 60;
  const endHour = Math.floor(endMinutes / 60);
  const endMin = endMinutes % 60;

  // Ensure we don't go past 23:59
  if (endHour >= 24) {
    return { start: '23:00', end: '23:59' };
  }

  const start = `${startHour.toString().padStart(2, '0')}:${startMin.toString().padStart(2, '0')}`;
  const end = `${endHour.toString().padStart(2, '0')}:${endMin.toString().padStart(2, '0')}`;

  return { start, end };
};

export const WorkingHoursStep = () => {
  const { state, dispatch } = useCustomerJourney();
  const { professionals } = state;
  const [selectedProfessionalId, setSelectedProfessionalId] = useState<string>('');
  const [isQuickAddOpen, setIsQuickAddOpen] = useState(false);
  const [copyFromProfessionalId, setCopyFromProfessionalId] = useState<string>('');
  const [isCopyDialogOpen, setIsCopyDialogOpen] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const selectedProfessional = professionals.find((p) => p.id === selectedProfessionalId);

  // Auto-select first professional for PRO plan
  useEffect(() => {
    if (state.plan !== PlanType.BUSINESS && professionals.length > 0 && !selectedProfessionalId) {
      setSelectedProfessionalId(professionals[0].id);
    }
  }, [state.plan, professionals, selectedProfessionalId]);

  // Only set selectedProfessionalId if it becomes invalid (professional was deleted)
  useEffect(() => {
    if (
      selectedProfessionalId &&
      professionals.length > 0 &&
      !professionals.find((p) => p.id === selectedProfessionalId)
    ) {
      setSelectedProfessionalId('');
    }
  }, [professionals, selectedProfessionalId]);

  useEffect(() => {
    if (
      selectedProfessional &&
      (!selectedProfessional.schedule || typeof selectedProfessional.schedule !== 'object')
    ) {
      const updatedProfessionals = professionals.map((p) => {
        if (p.id === selectedProfessional.id) {
          return {
            ...p,
            schedule: { ...emptySchedule }
          };
        }
        return p;
      });
      dispatch({ type: ActionType.UPDATE_PROFESSIONALS, payload: updatedProfessionals });
    }
  }, [selectedProfessional, dispatch, professionals]);

  useEffect(() => {
    const hasSeenGuide = localStorage.getItem('seen_schedule_guide');
    if (!hasSeenGuide) {
      const timer = setTimeout(() => {
        setShowGuide(true);
        localStorage.setItem('seen_schedule_guide', 'true');
      }, 500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleGuideClose = () => {
    setShowGuide(false);
    const hasSeenGuide = localStorage.getItem('seen_schedule_guide');
    const hasSeenQuickAddAfterGuide = localStorage.getItem('seen_quick_add_after_guide');

    if (hasSeenGuide === 'true' && !hasSeenQuickAddAfterGuide) {
      localStorage.setItem('seen_quick_add_after_guide', 'true');
      setTimeout(() => {
        setIsQuickAddOpen(true);
      }, 300);
    }
  };

  // Helper function to check if a professional has any time slots in their schedule
  const checkForTimeSlots = (professional: Professional) => {
    return Object.values(professional.schedule).some(
      (daySlots) => Array.isArray(daySlots) && daySlots.length > 0
    );
  };

  // Update professionals data and check for working hours
  const updateData = (updatedProfessionals: Professional[], targetProfessionalId?: string) => {
    // First update the professionals array
    dispatch({ type: ActionType.UPDATE_PROFESSIONALS, payload: updatedProfessionals });

    // Then check for time slots and update hasWorkingHours
    const professionalIdToUpdate = targetProfessionalId || selectedProfessionalId;
    const professionalToUpdate = updatedProfessionals.find((p) => p.id === professionalIdToUpdate);
    if (professionalToUpdate) {
      const hasWorkingHours = checkForTimeSlots(professionalToUpdate);

      // Use SET_WORKING_HOURS action
      dispatch({
        type: ActionType.SET_WORKING_HOURS,
        payload: {
          professionalId: professionalIdToUpdate,
          hasWorkingHours
        }
      });
    }
  };

  const handleAddTimeSlot = (day: DayKey) => {
    if (!selectedProfessional) return;

    const currentDaySlots = selectedProfessional.schedule[day] || [];
    const newTimeSlot = calculateSmartTimeSlot(currentDaySlots);

    const wouldOverlap = currentDaySlots.some((existingSlot) => {
      const newStart = timeToMinutes(newTimeSlot.start);
      const newEnd = timeToMinutes(newTimeSlot.end);
      const existingStart = timeToMinutes(existingSlot.start);
      const existingEnd = timeToMinutes(existingSlot.end);

      return newStart < existingEnd && newEnd > existingStart;
    });

    if (wouldOverlap) {
      toast.error('No se puede agregar este horario porque se superpone con otro horario existente.');
      return;
    }

    const updatedProfessionals = professionals.map((p) => {
      if (p.id === selectedProfessionalId) {
        return {
          ...p,
          schedule: {
            ...p.schedule,
            [day]: [...currentDaySlots, newTimeSlot]
          }
        };
      }
      return p;
    });

    updateData(updatedProfessionals);

    // Track time slot addition
    trackGenericEvent('Journey Working Hours Time Slot Added', {
      day,
      professionalId: selectedProfessionalId,
      totalSlots: currentDaySlots.length + 1
    });
  };

  const handleUpdateTimeSlot = (day: DayKey, index: number, field: 'start' | 'end', value: string) => {
    if (!selectedProfessional) return;

    const updatedProfessionals = professionals.map((p) => {
      if (p.id === selectedProfessionalId) {
        const updatedSlots = [...(p.schedule[day] || [])];
        if (index < updatedSlots.length) {
          updatedSlots[index] = {
            ...updatedSlots[index],
            [field]: value
          };
        }

        return {
          ...p,
          schedule: {
            ...p.schedule,
            [day]: updatedSlots
          }
        };
      }
      return p;
    });

    updateData(updatedProfessionals);

    // Track time slot update
    trackGenericEvent('Journey Working Hours Time Slot Updated', {
      day,
      professionalId: selectedProfessionalId,
      field,
      slotIndex: index
    });
  };

  const handleDeleteTimeSlot = (day: DayKey, index: number) => {
    if (!selectedProfessional) return;

    const updatedProfessionals = professionals.map((p) => {
      if (p.id === selectedProfessionalId) {
        const updatedSlots = (p.schedule[day] || []).filter((_, i) => i !== index);
        return {
          ...p,
          schedule: {
            ...p.schedule,
            [day]: updatedSlots
          }
        };
      }
      return p;
    });

    updateData(updatedProfessionals);

    // Track time slot deletion
    trackGenericEvent('Journey Working Hours Time Slot Deleted', {
      day,
      professionalId: selectedProfessionalId,
      slotIndex: index,
      remainingSlots: (selectedProfessional.schedule[day] || []).length - 1
    });
  };

  const handleQuickAddTimeSlot = (selectedDays: DayKey[], quickAddStart: string, quickAddEnd: string) => {
    const targetProfessionalId =
      selectedProfessionalId || (professionals.length > 0 ? professionals[0].id : '');
    const targetProfessional = professionals.find((p) => p.id === targetProfessionalId);

    if (!targetProfessional || selectedDays.length === 0) return;

    if (!selectedProfessionalId && targetProfessionalId) {
      setSelectedProfessionalId(targetProfessionalId);
    }

    const daysWithOverlap: DayKey[] = [];

    selectedDays.forEach((day) => {
      const currentDaySlots = targetProfessional.schedule[day] || [];
      const wouldOverlap = currentDaySlots.some((existingSlot) => {
        const newStart = timeToMinutes(quickAddStart);
        const newEnd = timeToMinutes(quickAddEnd);
        const existingStart = timeToMinutes(existingSlot.start);
        const existingEnd = timeToMinutes(existingSlot.end);

        return newStart < existingEnd && newEnd > existingStart;
      });

      if (wouldOverlap) {
        daysWithOverlap.push(day);
      }
    });

    if (daysWithOverlap.length > 0) {
      const dayNames = daysWithOverlap
        .map((dayKey) => days.find((d) => d.key === dayKey)?.label || dayKey)
        .join(', ');

      toast.error(
        `No se pueden agregar los horarios porque hay superposiciones en los siguientes días: ${dayNames}`
      );
      return;
    }

    const updatedProfessionals = professionals.map((p) => {
      if (p.id === targetProfessionalId) {
        const updatedSchedule = { ...p.schedule };

        selectedDays.forEach((day) => {
          const currentDaySlots = updatedSchedule[day] || [];
          updatedSchedule[day] = [...currentDaySlots, { start: quickAddStart, end: quickAddEnd }];
        });

        return {
          ...p,
          schedule: updatedSchedule
        };
      }
      return p;
    });

    updateData(updatedProfessionals, targetProfessionalId);

    // Track quick add time slot
    trackGenericEvent('Journey Working Hours Quick Add Used', {
      professionalId: targetProfessionalId,
      daysCount: selectedDays.length,
      timeRange: `${quickAddStart}-${quickAddEnd}`
    });

    setIsQuickAddOpen(false);
  };

  const handleQuickAddMultipleTimeSlots = (selectedDays: DayKey[], timeRanges: TimeRange[]) => {
    const targetProfessionalId =
      selectedProfessionalId || (professionals.length > 0 ? professionals[0].id : '');
    const targetProfessional = professionals.find((p) => p.id === targetProfessionalId);

    if (!targetProfessional || selectedDays.length === 0 || timeRanges.length === 0) return;

    if (!selectedProfessionalId && targetProfessionalId) {
      setSelectedProfessionalId(targetProfessionalId);
    }

    const daysWithOverlap: DayKey[] = [];

    // Check for overlaps across all time ranges for each day
    selectedDays.forEach((day) => {
      const currentDaySlots = targetProfessional.schedule[day] || [];

      const wouldOverlap = timeRanges.some((range) => {
        return currentDaySlots.some((existingSlot) => {
          const newStart = timeToMinutes(range.start);
          const newEnd = timeToMinutes(range.end);
          const existingStart = timeToMinutes(existingSlot.start);
          const existingEnd = timeToMinutes(existingSlot.end);

          return newStart < existingEnd && newEnd > existingStart;
        });
      });

      if (wouldOverlap) {
        daysWithOverlap.push(day);
      }
    });

    if (daysWithOverlap.length > 0) {
      const dayNames = daysWithOverlap
        .map((dayKey) => days.find((d) => d.key === dayKey)?.label || dayKey)
        .join(', ');

      toast.error(
        `No se pueden agregar los horarios porque hay superposiciones en los siguientes días: ${dayNames}`
      );
      return;
    }

    // Apply all time ranges at once
    const updatedProfessionals = professionals.map((p) => {
      if (p.id === targetProfessionalId) {
        const updatedSchedule = { ...p.schedule };

        selectedDays.forEach((day) => {
          const currentDaySlots = updatedSchedule[day] || [];
          const newTimeSlots = timeRanges.map((range) => ({ start: range.start, end: range.end }));
          updatedSchedule[day] = [...currentDaySlots, ...newTimeSlots];
        });

        return {
          ...p,
          schedule: updatedSchedule
        };
      }
      return p;
    });

    updateData(updatedProfessionals, targetProfessionalId);

    // Track multiple time slots quick add
    trackGenericEvent('Journey Working Hours Quick Add Multiple Used', {
      professionalId: targetProfessionalId,
      daysCount: selectedDays.length,
      timeRangesCount: timeRanges.length,
      timeRanges: timeRanges.map((range) => `${range.start}-${range.end}`)
    });

    setIsQuickAddOpen(false);
  };

  const handleOpenCopyDialog = () => {
    const professionalsWithSchedule = professionals.filter(
      (p) => p.id !== selectedProfessionalId && hasProfessionalSchedule(p)
    );

    if (professionalsWithSchedule.length > 0) {
      setCopyFromProfessionalId(professionalsWithSchedule[0].id);
      setIsCopyDialogOpen(true);

      // Track copy schedule dialog open
      trackGenericEvent('Journey Working Hours Copy Schedule Opened', {
        professionalId: selectedProfessionalId,
        availableToCopyFrom: professionalsWithSchedule.length
      });
    } else {
      alert('No hay otros profesionales con horarios configurados para copiar.');
    }
  };

  const handleCopySchedule = () => {
    if (!selectedProfessional || !copyFromProfessionalId) return;

    const sourceProfessional = professionals.find((p) => p.id === copyFromProfessionalId);
    if (!sourceProfessional) return;

    const updatedProfessionals = professionals.map((p) => {
      if (p.id === selectedProfessionalId) {
        return {
          ...p,
          schedule: JSON.parse(JSON.stringify(sourceProfessional.schedule))
        };
      }
      return p;
    });

    updateData(updatedProfessionals);
    setIsCopyDialogOpen(false);

    // Show success message
    toast.success('Horarios copiados exitosamente');

    // Track successful schedule copy
    trackGenericEvent('Journey Working Hours Schedule Copied', {
      fromProfessionalId: copyFromProfessionalId,
      toProfessionalId: selectedProfessionalId
    });
  };

  const handleSelectProfessional = (professionalId: string) => {
    setSelectedProfessionalId(professionalId);

    const selectedProf = professionals.find((p) => p.id === professionalId);
    const hasExistingSchedule = selectedProf ? hasProfessionalSchedule(selectedProf) : false;

    // Check if no other professionals have schedules configured
    const professionalsWithSchedule = professionals.filter((p) => hasProfessionalSchedule(p));
    const isFirstSelection = professionalsWithSchedule.length === 0;

    // Open DialogQuickAdd automatically if:
    // 1. The selected professional doesn't have existing schedules AND
    // 2. No other professionals have schedules (first selection)
    if (!hasExistingSchedule && isFirstSelection) {
      setIsQuickAddOpen(true);
    }

    // Track professional selection for working hours
    trackGenericEvent('Journey Working Hours Professional Selected', {
      professionalId,
      hasExistingSchedule,
      isFirstSelection
    });
  };

  const handleQuickAddOpen = () => {
    setIsQuickAddOpen(true);

    // Track quick add dialog open
    trackGenericEvent('Journey Working Hours Quick Add Opened', {
      professionalId: selectedProfessionalId
    });
  };

  return (
    <div className="space-y-5 mb-4">
      <div className="text-center mb-6">
        <motion.h1
          className="text-2xl font-bold"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          Horarios de trabajo
        </motion.h1>
        <motion.p
          className="text-gray-600 mt-2"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {state.plan === PlanType.BUSINESS
            ? 'Configura los horarios semanales recurrentes de cada profesional.'
            : 'Configura tus horarios semanales recurrentes.'}{' '}
          <span onClick={() => setShowGuide(true)} className="text-blue cursor-pointer hover:underline">
            Ver guía de horarios
          </span>
        </motion.p>
      </div>

      {/* Configuración de horarios */}
      <div
        className={`${state.plan === PlanType.BUSINESS ? 'flex flex-col sm:flex-row mt-4 gap-4' : 'mt-4'}`}
      >
        {state.plan === PlanType.BUSINESS && (
          <div className="w-full sm:w-1/3">
            <StaffSelector
              professionals={professionals}
              selectedProfessionalId={selectedProfessionalId}
              onSelectProfessional={handleSelectProfessional}
            />
          </div>
        )}
        <div className={`${state.plan === PlanType.BUSINESS ? 'flex-1 min-w-0' : 'w-full'}`}>
          {selectedProfessional ? (
            hasProfessionalSchedule(selectedProfessional) ? (
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    className="w-full border-blue text-blue !rounded-2xl border bg-white hover:bg-blue/5 transition-all duration-200"
                    onClick={handleQuickAddOpen}
                  >
                    <div className="flex items-center justify-center gap-3">
                      <Sparkles className="h-5 w-5" />
                      <span className="text-lg font-medium">Creación rápida</span>
                    </div>
                  </Button>
                </div>
                <WeeklyScheduleCards
                  professional={selectedProfessional}
                  days={days}
                  onAddTimeSlot={handleAddTimeSlot}
                  onUpdateTimeSlot={handleUpdateTimeSlot}
                  onDeleteTimeSlot={handleDeleteTimeSlot}
                />
              </div>
            ) : (
              <motion.div
                className="text-center py-12 px-6 border rounded-xl"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="max-w-md mx-auto">
                  <div className="mb-6">
                    <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500 text-sm leading-relaxed mb-6">
                      Elige cómo quieres configurar los horarios de trabajo.
                    </p>
                  </div>

                  <div className="space-y-3">
                    <Button
                      variant="outline"
                      className="w-full h-12 border-blue text-blue"
                      onClick={handleQuickAddOpen}
                    >
                      <div className="flex items-center justify-center gap-2">
                        <Sparkles className="h-5 w-5" />
                        <span>Agregar horarios</span>
                      </div>
                    </Button>

                    {state.plan === PlanType.BUSINESS && (
                      <Button
                        variant="outline"
                        className="w-full h-12 border-gray-300 text-gray-700 hover:bg-gray-50"
                        onClick={handleOpenCopyDialog}
                        disabled={
                          professionals.filter(
                            (p) => p.id !== selectedProfessionalId && hasProfessionalSchedule(p)
                          ).length === 0
                        }
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Copy className="h-5 w-5" />
                          <span>Copiar horarios</span>
                        </div>
                      </Button>
                    )}
                  </div>
                </div>
              </motion.div>
            )
          ) : (
            <motion.div
              className="text-center py-12 px-6 border rounded-xl"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="max-w-md mx-auto">
                <div className="mb-6">
                  <Calendar className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {state.plan === PlanType.BUSINESS
                      ? 'Selecciona un profesional'
                      : 'Configura tus horarios de trabajo'}
                  </h3>
                  <p className="text-gray-500 text-sm leading-relaxed">
                    {state.plan === PlanType.BUSINESS
                      ? 'Selecciona un profesional de la lista para comenzar a configurar sus horarios de trabajo.'
                      : 'Comienza configurando tus horarios semanales recurrentes.'}
                  </p>
                </div>
                {state.plan !== PlanType.BUSINESS && (
                  <Button
                    variant="outline"
                    className="w-full h-12 border-blue text-blue"
                    onClick={handleQuickAddOpen}
                  >
                    <div className="flex items-center justify-center gap-2">
                      <Sparkles className="h-5 w-5" />
                      <span>Agregar horarios</span>
                    </div>
                  </Button>
                )}
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <DialogQuickAdd
        isOpen={isQuickAddOpen}
        onOpenChange={(open) => {
          setIsQuickAddOpen(open);
        }}
        onAddTimeSlot={handleQuickAddTimeSlot}
        onAddMultipleTimeSlots={handleQuickAddMultipleTimeSlots}
      />

      <DialogClone
        isOpen={isCopyDialogOpen}
        onOpenChange={setIsCopyDialogOpen}
        professionals={professionals}
        selectedProfessionalId={selectedProfessionalId}
        copyFromProfessionalId={copyFromProfessionalId}
        setCopyFromProfessionalId={setCopyFromProfessionalId}
        onCopySchedule={handleCopySchedule}
      />

      <DialogGuide isOpen={showGuide} onClose={handleGuideClose} />
    </div>
  );
};
